import React from 'react'
import clsx from 'clsx'
import Icon from '@/components/Icon/Icon'
import Button from '@/components/Button/Button'
import PropTypes from 'prop-types'
import { ButtonType, ButtonVariant } from '@/components/Button/Button.type'

export const ChipType = {
  FILTER: 'filter',
  INPUT: 'input',
}

const CheckIcon = () => <Icon name="check" size="small" style="outlined" data-testid="check-icon" />

const RemoveIcon = () => (
  <Icon name="x-mark" size="small" style="outlined" data-testid="remove-icon" />
)

const Chips = ({
  label,
  size = 'default',
  type,
  disabled = false,
  readOnly = false,
  selected,
  onClick,
  onRemove,
  onRemoveTooltipText,
  addClass,
}) => {
  const isDisabledSelected = disabled && selected

  const chipClass = clsx(
    'flex items-center box-border rounded-[20px] border [&_button]:gap-0 [&_button]:whitespace-nowrap',
    addClass,
    {
      'h-9': size !== 'small',
      'h-6': size === 'small',
      '[&_button]:py-1 [&_button]:px-2': size === 'small',
      // default state (not disabled, not selected, not readOnly)
      'text-grey-50 border-grey-80 hover:text-grey-30 hover:border-grey-90 hover:bg-grey-95':
        !disabled && !selected && !(readOnly && type === ChipType.INPUT),
      // readOnly input chip
      'text-grey-10 border-grey-50 bg-white': readOnly && type === ChipType.INPUT && !disabled,
      // filter chip active states
      'cursor-pointer active:text-grey-10 active:border-grey-50':
        type === ChipType.FILTER && !selected && !disabled,
      'cursor-pointer active:text-theme-20 active:[&_svg_path]:fill-theme-20':
        type === ChipType.FILTER && selected && !disabled,
      // disabled + selected (overrides both individual states)
      'text-grey-50 border-transparent bg-grey-95 [&_svg_path]:fill-grey-50 pointer-events-none':
        isDisabledSelected,
      // disabled only
      'text-grey-80 border-grey-90 bg-transparent [&_svg_path]:fill-grey-80 pointer-events-none hover:text-grey-80 hover:border-grey-90':
        disabled && !selected,
      // selected only
      'text-theme-50 border-transparent bg-theme-98 [&_svg_path]:fill-theme-50 hover:text-theme-40 hover:bg-theme-95':
        !disabled && selected,
    }
  )

  const chipLabelClass = clsx(
    'type-body-1 max-phone:inline-block max-phone:align-middle max-phone:overflow-hidden max-phone:text-ellipsis max-phone:whitespace-nowrap',
    {
      'px-1': size !== 'small',
      'type-meta-medium px-0.5': size === 'small',
    }
  )

  const handleClick = e => {
    e.stopPropagation()
    if (disabled || readOnly) return

    if (onRemove) {
      return onRemove()
    }

    if (onClick) {
      return onClick()
    }
  }

  return (
    <Button
      type={ButtonType.CHIP}
      variant={ButtonVariant.CHIP}
      onClick={handleClick}
      prefixIcon={type === ChipType.FILTER && selected ? <CheckIcon /> : null}
      suffixIcon={!disabled && !readOnly && onRemove ? <RemoveIcon /> : null}
      text={<span className={chipLabelClass}>{label}</span>}
      tooltipText={onRemoveTooltipText}
      addbtnClass={chipClass} // Apply chipClass to the button's className
    />
  )
}

Chips.propTypes = {
  label: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['default', 'small']),
  type: PropTypes.oneOf(Object.values(ChipType)).isRequired,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  onRemove: PropTypes.func,
  onRemoveTooltipText: PropTypes.string,
  addClass: PropTypes.string,
}

export default Chips
